import type { Product, SelectedProductModifier, TabItemModifier } from '@/types';

type ModifierRef = { modifierId: string | null };
type PricedModifier = { price: number };
type NamedModifier = { name: string };

/** Hacienda `Detalle` max length. */
const INVOICE_DESCRIPTION_MAX_LENGTH = 200;

const roundMoney = (value: number) => Math.round(value * 100) / 100;

/** Stands in for a modifier deleted after it was applied; new lines never carry it, so they never merge. */
const DELETED_MODIFIER_KEY = 'deleted';

/**
 * Order-independent identity of a line's modifiers, used to decide whether two
 * lines of the same product can be merged. Empty string when there are none.
 */
export const modifierSignature = (modifiers?: ModifierRef[] | null): string => {
  if (!modifiers?.length) return '';
  return modifiers
    .map((modifier) => modifier.modifierId ?? DELETED_MODIFIER_KEY)
    .sort()
    .join(',');
};

export const hasModifiers = (modifiers?: unknown[] | null): boolean => Boolean(modifiers?.length);

export const modifiersTotal = (modifiers?: PricedModifier[] | null): number =>
  roundMoney((modifiers ?? []).reduce((sum, modifier) => sum + (Number(modifier.price) || 0), 0));

/** Unit price charged for a line: product price plus its modifiers. */
export const lineUnitPrice = (
  product: Pick<Product, 'price'>,
  modifiers?: PricedModifier[] | null,
): number => roundMoney(Number(product.price) + modifiersTotal(modifiers));

export const modifierNames = (modifiers?: NamedModifier[] | null): string[] =>
  (modifiers ?? []).map((modifier) => modifier.name);

/**
 * Kitchen ticket notes snapshot: one "● name" line per modifier, then the free
 * notes. Uses the same bullet the text-only modifiers write into notes.
 */
export const composeKitchenNotes = (
  notes?: string | null,
  modifiers?: NamedModifier[] | null,
): string | undefined => {
  const lines = modifierNames(modifiers).map((name) => `● ${name}`);
  const trimmedNotes = notes?.trim();
  if (trimmedNotes) lines.push(trimmedNotes);
  return lines.length ? lines.join('\n') : undefined;
};

/** Electronic invoice line description: "Café + Leche extra + Shot". */
export const composeInvoiceDescription = (
  productName: string,
  modifiers?: NamedModifier[] | null,
): string => {
  const description = [productName, ...modifierNames(modifiers)].join(' + ');
  return description.length > INVOICE_DESCRIPTION_MAX_LENGTH
    ? description.slice(0, INVOICE_DESCRIPTION_MAX_LENGTH)
    : description;
};

/** Client-side TabItem modifiers for optimistic rows and dispatch prints (ids are local). */
export const toTabItemModifiers = (
  tabItemId: string,
  modifiers?: SelectedProductModifier[] | null,
): TabItemModifier[] | undefined =>
  modifiers?.length
    ? modifiers.map((modifier) => ({
        id: `${tabItemId}:${modifier.modifierId}`,
        tabItemId,
        modifierId: modifier.modifierId,
        name: modifier.name,
        price: modifier.price,
      }))
    : undefined;

/** Admin price input: empty means free; otherwise a finite, non-negative number. */
export const isValidModifierPrice = (value: string): boolean => {
  if (value.trim() === '') return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0;
};

/** Adds or removes a modifier; a modifier applies at most once per line. */
export const toggleSelectedModifier = (
  selected: SelectedProductModifier[],
  modifier: SelectedProductModifier,
): SelectedProductModifier[] =>
  selected.some((entry) => entry.modifierId === modifier.modifierId)
    ? selected.filter((entry) => entry.modifierId !== modifier.modifierId)
    : [...selected, modifier];
